import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFish } from '@fortawesome/free-solid-svg-icons';
import SimpleDropdownPopup from '../ui/SimpleDropdownPopup';
import { BackendChangeIndicator } from '../common/BackendChangeIndicator';

// Hilfsfunktion für einheitliche "Kein Wert" Anzeige
const renderEmptyValue = (value: any, fallback = 'Kein Wert') => {
  if (!value || value === '' || value === null || value === undefined) {
    return <span className="text-xs text-gray-500 italic">{fallback}</span>;
  }
  return value;
};

// Hilfsfunktion für Kapitalisierung der ersten Buchstaben
const capitalizeFirstLetter = (str: string): string => {
  if (!str || typeof str !== 'string') return str;
  return str.trim().charAt(0).toUpperCase() + str.trim().slice(1);
};

interface FischTabProps {
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  isSupplierMode?: boolean;
  originalData?: any;
  articleNumber?: string;
}

export const FischTab: React.FC<FischTabProps> = ({ 
  data, 
  mode, 
  onChange,
  isSupplierMode = false,
  originalData,
  articleNumber
}) => {

  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Change tracking only for Admin (not Supplier mode)
    if (isSupplierMode || !articleNumber) {
      return content;
    }
    
    return (
      <BackendChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        articleNumber={articleNumber}
      >
        {content}
      </BackendChangeIndicator>
    );
  };
  return (
    <div className="space-y-6">
      {/* Fisch Informationen */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fangzone */}
          <div>
            {renderFieldWithHistory(
              'Fangzone',
              'fangzone',
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="fangzone"
                  value={data?.fangzone || ''}
                  onChange={(value: string) => onChange('fangzone', value)}
                  label="Fangzone"
                  placeholder="Fangzone auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fangzone</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(capitalizeFirstLetter(data?.fangzone))}</p>
                </div>
              )
            )}
          </div>

          {/* Fangmethode */}
          <div>
            {renderFieldWithHistory(
              'Fangmethode',
              'fangmethode',
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="fangmethode"
                  value={data?.fangmethode || ''}
                  onChange={(value: string) => onChange('fangmethode', value)}
                  label="Fangmethode"
                  placeholder="Fangmethode auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fangmethode</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(capitalizeFirstLetter(data?.fangmethode))}</p>
                </div>
              )
            )}
          </div>

          {/* Lateinischer Name */}
          <div>
            {renderFieldWithHistory(
              'Lateinischer Name',
              'scientificName',
              mode === 'edit' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lateinischer Name</label>
                  <input
                    type="text"
                    value={data?.scientificName || ''}
                    onChange={(e) => onChange('scientificName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Wissenschaftlicher Artname..."
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lateinischer Name</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(capitalizeFirstLetter(data?.scientificName))}</p>
                </div>
              )
            )}
          </div>

          {/* Produktionsmethode */}
          <div>
            {renderFieldWithHistory(
              'Produktionsmethode',
              'productionMethod',
              mode === 'edit' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Produktionsmethode</label>
                  <select
                    value={data?.productionMethod || ''}
                    onChange={(e) => onChange('productionMethod', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Auswählen...</option>
                    <option value="Aus Binnenfischerei in">Aus Binnenfischerei in</option>
                    <option value="Gefangen im">Gefangen im</option>
                    <option value="In Aquakultur gewonnen">In Aquakultur gewonnen</option>
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Produktionsmethode</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(capitalizeFirstLetter(data?.productionMethod))}</p>
                </div>
              )
            )}
          </div>

          {/* Auftauhinweis */}
          <div className="md:col-span-2">
            {renderFieldWithHistory(
              'Auftauhinweis',
              'thawingInstructions',
              mode === 'edit' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Auftauhinweis</label>
                  <textarea
                    value={data?.thawingInstructions || ''}
                    onChange={(e) => onChange('thawingInstructions', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Auftauhinweis..."
                    rows={3}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Auftauhinweis</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(capitalizeFirstLetter(data?.thawingInstructions))}</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
